import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import useDialog from '../../../../web-app-components/hooks/useDialog';
import ButtonSlim from '../../../../web-app-components/components/ButtonSlim';
import { Formik, useFormikContext } from 'formik';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import objectsClient from '../../../../clients/objects';
import objectsSuggestedClient from '../../../../clients/objects-suggested';

import { toForm, fromForm } from '../../utils/formData';
import prepareFiles from '../../utils/prepareFiles';

import Form from '../../components/Form';

import ConfirmRemoveDialog from './components/ConfirmRemoveDialog';

// Component to handle locale changes (only when user changes the dropdown, not when we set locale from load)
const LocaleChangeHandler = ({ currentLocale, onLocaleChange }) => {
  const { values } = useFormikContext();
  const prevFormLocaleRef = useRef(values.locale);
  const onLocaleChangeRef = useRef(onLocaleChange);
  onLocaleChangeRef.current = onLocaleChange;

  useEffect(() => {
    const formLocale = values.locale;
    const formLocaleChanged = formLocale !== prevFormLocaleRef.current;
    prevFormLocaleRef.current = formLocale;

    // Only react when the form's locale field actually changed (user selected a new locale)
    if (formLocaleChanged && formLocale !== currentLocale) {
      onLocaleChangeRef.current(formLocale);
    }
  }, [values.locale, currentLocale]);

  return null;
};

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    marginBottom: theme.spacing(2),
  },
  title: {
    flex: 1,
  },
}));

const fieldIsEmpty = (value) => {
  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length === 0;
  }
  if(typeof value === 'object'){   
    return value === null
  }
  return typeof value !== 'number';
};


const SuggestedObject = ({ id }) => {
  const classes = useStyles();

  const history = useHistory();
  const { path } = useRouteMatch();

  const { enqueueSnackbar } = useSnackbar();

  const confirmRemoveDialog = useDialog();

  const [data, setData] = useState();
  const [object, setObject] = useState();
  const [currentLocale, setCurrentLocale] = useState('lt-LT');
  const formikRef = useRef(null);
  const prevLocaleRef = useRef('lt-LT');

  const createMissingLocale = async (objectId, locale, sourceData) => {
    if (!sourceData) return null;
    const payload = fromForm({
      ...toForm(sourceData),
      locale,
    });
    return objectsClient.createLocalization(objectId, payload, locale);
  };

  const loadObjectForLocale = async (objectId, locale, updateForm = true, attemptedCreate = false) => {
    try {
      const localeObject = await objectsClient.findOne(objectId, locale);
      setObject(localeObject);
      setCurrentLocale(locale);
      prevLocaleRef.current = locale;
      // Update form values if formik is available and updateForm is true
      if (updateForm && formikRef.current) {
        formikRef.current.setValues(toForm({
          ...localeObject,
          locale: locale,
        }));
      }
    } catch (error) {
      // If locale doesn't exist, try creating it from default locale
      if (locale !== 'lt-LT') {
        try {
          const ltObject = object || await objectsClient.findOne(objectId, 'lt-LT');
          if (error?.response?.status === 404 && !attemptedCreate && ltObject) {
            await createMissingLocale(objectId, locale, ltObject);
            return loadObjectForLocale(objectId, locale, updateForm, true);
          }

          setObject(ltObject);
          setCurrentLocale('lt-LT');
          prevLocaleRef.current = 'lt-LT';
          if (updateForm && formikRef.current) {
            formikRef.current.setValues(toForm({
              ...ltObject,
              locale: 'lt-LT',
            }));
          }
        } catch (ltError) {
          console.warn('Failed to load or create locale', ltError);
          // Object might not exist
          setObject(null);
        }
      } else {
        setObject(null);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await objectsSuggestedClient.findOne(id);
        setData(response);
        const suggestedLocale = response?.suggestedLocale === 'en-EU' ? 'en-EU' : 'lt-LT';
        if (response?.object) {
          await loadObjectForLocale(response.object.id, suggestedLocale, false);
        } else {
          setCurrentLocale(suggestedLocale);
          prevLocaleRef.current = suggestedLocale;
        }
      } catch (error) {
        enqueueSnackbar(
          'Nepavyko gauti objekto. Bandykite atidaryti iš naujo.',
          { variant: 'error' }
        );
      }
    })();
  }, [id, enqueueSnackbar]);

  const handleRemoveClick = async () => confirmRemoveDialog.setOpen(true);

  const handleRemoveConfirm = async () => {
    try {
      await objectsSuggestedClient.remove(id);
      enqueueSnackbar('Pasiūlymas pašalintas', { variant: 'success' });
      history.push(`${path.replace('/:id', '')}`);
    } catch (error) {
      enqueueSnackbar('Nepavyko pašalinti pasiūlymo. Bandykite dar kartą.', {
        variant: 'error',
      });
    }
  };

  const handleSubmit = async (values) => {
    try {     
      let newValues = Object.entries(values).reduce((obj, [name, value]) => {       
        if (!fieldIsEmpty(value)) {
          obj[name] = value;
        }
        return obj;
      }, {});

      if(Object.keys(newValues).length === 0){
        return  enqueueSnackbar('Nėra duomenų atnaujinimui', {
          variant: 'error',
        }); 
      }

      if(Array.isArray(newValues.Pictures)) newValues.Pictures = await prepareFiles(newValues.Pictures);
      if(Array.isArray(newValues.AudioRecordings)) newValues.AudioRecordings = await prepareFiles(newValues.AudioRecordings);

      const formData = fromForm(newValues);
      const { locale, ...objectData } = formData;

      if(!data?.object){
        // Create new object in the selected locale (from form, defaults to suggested object's locale)
        const createLocale = locale === 'en-EU' ? 'en-EU' : 'lt-LT';
        const dataForLocale = createLocale === 'lt-LT'
          ? (locale === 'lt-LT' ? objectData : { ...objectData, Title: '', Description: '' })
          : (locale === 'en-EU' ? objectData : { ...objectData, Title: '', Description: '' });

        const { data: { documentId: newId } } = await objectsClient.create(dataForLocale, createLocale);

        // If form has the other locale filled, create that localization too
        const otherLocale = createLocale === 'lt-LT' ? 'en-EU' : 'lt-LT';
        if (locale === otherLocale && (objectData.Title || objectData.Description)) {
          try {
            await objectsClient.createLocalization(newId, objectData, otherLocale);
          } catch (error) {
            console.warn('Failed to create other locale localization:', error);
          }
        }

        enqueueSnackbar('Naujas objektas sukurtas', { variant: 'success' });
        await objectsSuggestedClient.accept(id);
        history.push(path.replace('/:id', ''));
        return;
      } else {
        // Update existing object - only update the selected locale
        try {
          await objectsClient.update(data.object.id, objectData, locale);
        } catch (error) {
          // If update fails and locale is not default, try creating localization
          if (locale !== 'lt-LT') {
            try {
              await objectsClient.createLocalization(data.object.id, objectData, locale);
            } catch (createError) {
              console.error('Failed to create/update localization:', createError);
              const errorMessage = createError?.response?.data?.error?.message || 
                                   createError?.message || 
                                   'Nepavyko sukurti lokalizacijos';
              enqueueSnackbar(
                `Nepavyko atnaujinti objekto lokalizacijos (${locale}): ${errorMessage}`,
                { variant: 'error' }
              );
              throw createError;
            }
          } else {
            throw error;
          }
        }
        
        enqueueSnackbar('Esamas objektas atnaujintas', { variant: 'success' });
        // Reload object for the updated locale
        await loadObjectForLocale(data.object.id, locale, true);
        await objectsSuggestedClient.accept(id);
        history.push(path.replace('/:id', ''));
        return;
      }

    } catch (error) {
      console.warn(error)
      enqueueSnackbar('Nepavyko patvirtinti pasiūlymo. Bandykite dar kartą.', {
        variant: 'error',
      }); 
    }

  };
 
  return data ? (
    <div>
      <div className={classes.header}>
        <Typography className={classes.title} variant="h5">
          {data?.object ? <>Esamas Objektas "<a target='_blank' href={`/objects/${data?.object?.id}`} rel="noreferrer">{data?.object?.Title}</a>"</> : `Naujas Objektas "${data.Title}"`}
        </Typography>
        <ButtonSlim onClick={handleRemoveClick}>Pašalinti pasiūlymą</ButtonSlim>
      </div>
      <Formik
        key={`suggested-form-${id}-${currentLocale}`}
        innerRef={formikRef}
        initialValues={toForm({
          ...(object || data),
          locale: object ? currentLocale : (data?.suggestedLocale === 'en-EU' ? 'en-EU' : 'lt-LT'),
        })}
        // validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(props) => (
          <>
            {data?.object && (
              <LocaleChangeHandler
                currentLocale={currentLocale}
                onLocaleChange={(locale) => {
                  loadObjectForLocale(data.object.id, locale, true);
                }}
              />
            )}
            <Form {...props} suggested objectId={data?.object?.id} />
          </>
        )}
      </Formik>
      <ConfirmRemoveDialog
        {...confirmRemoveDialog}
        onConfirm={handleRemoveConfirm}
      />
    </div>
  ) : (
    <CircularProgress />
  );
};

SuggestedObject.propTypes = {
  id: PropTypes.string.isRequired,
};

export default SuggestedObject;
